
import { parseCSV, validateCSVStructure } from "./csvUtils";

/**
 * Utility functions for fetching and processing spreadsheet data
 */

/**
 * Extract spreadsheet ID from a Google Sheets URL
 */
export function extractSpreadsheetId(url: string): string {
  const match = url.match(/\/d\/([^/]+)/);
  if (!match) {
    throw new Error("Invalid Google Sheets URL");
  }
  return match[1];
}

/**
 * Fetch a Google Spreadsheet as CSV
 */
export async function fetchSpreadsheetAsCSV(spreadsheetId: string): Promise<string[][]> {
  const csvUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/export?format=csv`;
  
  const response = await fetch(csvUrl);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch spreadsheet: ${response.status} ${response.statusText}`);
  }
  
  const csvText = await response.text();
  
  // Parse CSV content
  try {
    const parsedData = parseCSV(csvText);
    // Validate the structure
    validateCSVStructure(parsedData);
    return parsedData;
  } catch (error) {
    console.error("Error processing spreadsheet data:", error);
    throw new Error(`Spreadsheet format error: ${error instanceof Error ? error.message : 'Unknown issue'}`);
  }
}

/**
 * Find column indices from headers
 */
export function findColumnIndices(headers: string[]): { 
  dateIndex: number, 
  wordIndex: number, 
  ruleIndex: number, 
  reasoningIndex: number,
  spectrumIndex: number,
  linkIndices: number[] // Changed to an array of indices for individual links
} {
  // Convert headers to lowercase for case-insensitive matching
  const normalizedHeaders = headers.map(header => header.toLowerCase().trim());
  
  // Find the header indices - log all headers to help debugging
  console.log('All headers found:', normalizedHeaders);
  
  const dateIndex = normalizedHeaders.indexOf('date');
  const wordIndex = normalizedHeaders.indexOf('words');
  const ruleIndex = normalizedHeaders.indexOf('logic'); // Using 'logic' column name
  
  // Check for reasoning column with variations in naming
  let reasoningIndex = normalizedHeaders.indexOf('reasoning');
  if (reasoningIndex === -1) {
    reasoningIndex = normalizedHeaders.indexOf('reason');
  }
  if (reasoningIndex === -1) {
    reasoningIndex = normalizedHeaders.indexOf('explanation');
  }
  
  const spectrumIndex = normalizedHeaders.indexOf('spectrum');
  
  // Find all link columns - EXPLICITLY look for columns F through J by name first
  const linkIndices: number[] = [];
  const letterColumns = ['f', 'g', 'h', 'i', 'j'];
  
  // First pass: look specifically for F-J columns
  letterColumns.forEach(letter => {
    const index = normalizedHeaders.indexOf(letter);
    if (index !== -1) {
      console.log(`Found letter column ${letter.toUpperCase()} at index ${index}`);
      linkIndices.push(index);
    }
  });
  
  // Second pass: if we didn't find all 5 letter columns, look for columns with "link" or "url"
  if (linkIndices.length < 5) {
    normalizedHeaders.forEach((header, index) => {
      // Only add if not already found as a letter column
      if (!linkIndices.includes(index) && (header.includes('link') || header.includes('url'))) {
        console.log(`Found additional link column "${headers[index]}" at index ${index}`);
        linkIndices.push(index);
      }
    });
  }
  
  // Sort link indices to maintain column order (important for matching with words)
  linkIndices.sort((a, b) => a - b);
  
  // If no specific link columns found at all, fall back to single "word links" column
  if (linkIndices.length === 0) {
    let wordLinksIndex = normalizedHeaders.indexOf('word links');
    if (wordLinksIndex === -1) {
      wordLinksIndex = normalizedHeaders.indexOf('wordlinks');
    }
    if (wordLinksIndex === -1) {
      wordLinksIndex = normalizedHeaders.indexOf('links');
    }
    
    if (wordLinksIndex !== -1) {
      console.log(`Found fallback word links column at index ${wordLinksIndex}`);
      linkIndices.push(wordLinksIndex);
    }
  }
  
  // Force log all column indices to help debug
  console.log('FORCED LOG - Column indices:', {
    dateIndex,
    wordIndex,
    ruleIndex,
    reasoningIndex,
    spectrumIndex,
    letterColumns,
    linkIndices,
    allColumnHeaders: headers
  });
  
  if (dateIndex === -1 || wordIndex === -1 || ruleIndex === -1) {
    throw new Error("Required columns not found in spreadsheet (date, words, logic)");
  }
  
  return { dateIndex, wordIndex, ruleIndex, reasoningIndex, spectrumIndex, linkIndices };
}

/**
 * Find a row for a specific date
 */
export function findRowByDate(rows: string[][], dateIndex: number, targetDate: string): string[] | undefined {
  // Try exact match first
  const exactMatch = rows.slice(1).find(row => {
    const rowDate = row[dateIndex].trim();
    return rowDate === targetDate;
  });
  
  if (exactMatch) {
    return exactMatch;
  }
  
  // If no exact match, try to parse dates and find the closest match
  console.log('No exact date match, trying to parse dates...');
  
  const targetDateTime = new Date(targetDate).getTime();
  
  // Find the row with a date closest to the target date
  return rows.slice(1).reduce((closest, currentRow) => {
    // Skip empty rows
    if (!currentRow[dateIndex]) return closest;
    
    try {
      const rowDate = new Date(currentRow[dateIndex].trim()).getTime();
      const closestDate = closest ? new Date(closest[dateIndex].trim()).getTime() : 0;
      
      // Calculate the absolute difference between dates
      const currentDiff = Math.abs(rowDate - targetDateTime);
      const closestDiff = Math.abs(closestDate - targetDateTime);
      
      // Return the row with the date closest to the target date
      return currentDiff < closestDiff ? currentRow : closest;
    } catch (err) {
      console.warn('Error parsing date:', currentRow[dateIndex]);
      return closest;
    }
  }, undefined as string[] | undefined);
}
